import { c as _c } from "react/compiler-runtime";
import classNames from 'classnames';
import Image, { type ImageProps } from 'next/image';
import Link from 'next/link';
import ButtonLink from '../ButtonLink';
import { LineIcon } from '../icons/icons';
import styles from './ProgramCard.module.scss';
import { minWidth } from '../tokens/breakpoints';

// Internal icons (Phase 4 PR 8): inline SVG in currentColor, hidden from
// assistive technology; the text beside each one carries the meaning. They
// replace the FontAwesome glyphs, so Helios renders no icon font here. The
// glyphs are Helios's line icons (icons/icons.tsx, O104).
const META_ICON = styles['helios-program-card__icon'];
export interface ProgramCardProps {
  /** URL the card links to. Omit to render a non-interactive preview card (no link, no CTA). */
  href?: string;
  image?: ImageProps['src'];
  imageAlt?: string;
  title: string;
  /** Optional short description shown below the title. */
  description?: string;
  type?: string;
  duration?: string;
  modality?: string;
  location?: string;
  /** Renders a calendar chip below the description (e.g. "Meets twice per week"). */
  schedule?: string;
  badge?: string;
  /** 'card' (default): vertical card on the subtle surface. 'list': a hairline row — title/meta left, CTA right; consecutive rows share one rule. */
  layout?: 'card' | 'list';
  /** CTA label. Defaults to `'Learn more'`. */
  ctaLabel?: string;
  /** Heading element rendered for `title`. Defaults to `'h4'`. */
  titleAs?: 'h2' | 'h3' | 'h4';
  /**
   * `false`: the card links with a plain `<a>` (no next/link, no prefetch), a
   * document navigation, as ButtonLink's prop: for a link to another site,
   * which changes the site. Defaults to client navigation.
   */
  clientNavigation?: boolean;
}
function ProgramCard(t0) {
  const $ = _c(62);
  const {
    href,
    image,
    imageAlt,
    title,
    description,
    type,
    duration,
    modality,
    location,
    schedule,
    badge,
    layout: t1,
    ctaLabel: t2,
    titleAs: t3,
    clientNavigation: t4
  } = t0;
  const layout = t1 === undefined ? "card" : t1;
  const ctaLabel = t2 === undefined ? "Learn more" : t2;
  const Heading = t3 === undefined ? "h4" : t3;
  const clientNavigation = t4 === undefined ? true : t4;
  const hasMeta = duration || modality || location || schedule;
  let t5;
  if ($[0] !== badge || $[1] !== image || $[2] !== imageAlt || $[3] !== title) {
    t5 = image && <div className={styles["helios-program-card__image"]}><Image src={image} alt={imageAlt ?? title} fill={true} sizes={`${minWidth("xl")} 33vw, ${minWidth("xs")} 50vw, 100vw`} style={{
        objectFit: "cover"
      }} />{badge && <span className={styles["helios-program-card__badge"]}>{badge}</span>}</div>;
    $[0] = badge;
    $[1] = image;
    $[2] = imageAlt;
    $[3] = title;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  const imageEl = t5;
  let t6;
  if ($[5] !== duration || $[6] !== hasMeta || $[7] !== location || $[8] !== modality || $[9] !== schedule) {
    t6 = hasMeta && <p className={styles["helios-program-card__meta"]}>{duration && <span className={styles["helios-program-card__meta-item"]}><LineIcon className={META_ICON} name="clock" />{duration}</span>}{modality && <span className={styles["helios-program-card__meta-item"]}><LineIcon className={META_ICON} name="globe" />{modality}</span>}{location && <span className={styles["helios-program-card__meta-item"]}><LineIcon className={META_ICON} name="map-pin" />{location}</span>}{schedule && <span className={styles["helios-program-card__meta-item"]}><LineIcon className={META_ICON} name="calendar" />{schedule}</span>}</p>;
    $[5] = duration;
    $[6] = hasMeta;
    $[7] = location;
    $[8] = modality;
    $[9] = schedule;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const metaEl = t6;
  let t7;
  if ($[11] !== Heading || $[12] !== title) {
    t7 = <Heading className={styles["helios-program-card__title"]}>{title}</Heading>;
    $[11] = Heading;
    $[12] = title;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== description) {
    t8 = description && <p className={styles["helios-program-card__description"]}>{description}</p>;
    $[14] = description;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== type) {
    t9 = type && <p className={styles["helios-program-card__type"]}>{type}</p>;
    $[16] = type;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== metaEl || $[19] !== t7 || $[20] !== t8 || $[21] !== t9) {
    t10 = <div className={styles["helios-program-card__body"]}>{t7}{t8}{t9}{metaEl}</div>;
    $[18] = metaEl;
    $[19] = t7;
    $[20] = t8;
    $[21] = t9;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  const bodyEl = t10;
  const t11 = layout === "list" && styles["helios-program-card--list"];
  let t12;
  if ($[23] !== t11) {
    t12 = classNames(styles["helios-program-card"], t11);
    $[23] = t11;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  const rootClass = t12;
  if (layout === "list") {
    let t13;
    if ($[25] !== Heading || $[26] !== title) {
      t13 = <Heading className={styles["helios-program-card__title"]}>{title}</Heading>;
      $[25] = Heading;
      $[26] = title;
      $[27] = t13;
    } else {
      t13 = $[27];
    }
    let t14;
    if ($[28] !== type) {
      t14 = type && <p className={styles["helios-program-card__type"]}>{type}</p>;
      $[28] = type;
      $[29] = t14;
    } else {
      t14 = $[29];
    }
    let t15;
    if ($[30] !== metaEl || $[31] !== t13 || $[32] !== t14) {
      t15 = <div className={styles["helios-program-card__list-body"]}>{t13}{t14}{metaEl}</div>;
      $[30] = metaEl;
      $[31] = t13;
      $[32] = t14;
      $[33] = t15;
    } else {
      t15 = $[33];
    }
    let t16;
    if ($[34] !== clientNavigation || $[35] !== ctaLabel || $[36] !== href || $[37] !== title) {
      t16 = href && <ButtonLink href={href} variant="outline" tone="neutral" size="sm" aria-label={`${ctaLabel} about ${title}`} arrow={true} {...clientNavigation ? {} : {
        clientNavigation: false as const
      }}>{ctaLabel}</ButtonLink>;
      $[34] = clientNavigation;
      $[35] = ctaLabel;
      $[36] = href;
      $[37] = title;
      $[38] = t16;
    } else {
      t16 = $[38];
    }
    let t17;
    if ($[39] !== t15 || $[40] !== t16) {
      t17 = <div className={styles["helios-program-card__list-row"]}>{t15}{t16}</div>;
      $[39] = t15;
      $[40] = t16;
      $[41] = t17;
    } else {
      t17 = $[41];
    }
    let t18;
    if ($[42] !== rootClass || $[43] !== t17) {
      t18 = <div className={rootClass}>{t17}</div>;
      $[42] = rootClass;
      $[43] = t17;
      $[44] = t18;
    } else {
      t18 = $[44];
    }
    return t18;
  }
  if (href) {
    const CardLink = clientNavigation ? Link : "a";
    const t13 = `${ctaLabel} about ${title}`;
    let t14;
    if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
      t14 = <LineIcon className={styles["helios-program-card__cta-arrow"]} name="arrow" />;
      $[45] = t14;
    } else {
      t14 = $[45];
    }
    let t15;
    if ($[46] !== ctaLabel) {
      t15 = <span className={styles["helios-program-card__cta"]}>{ctaLabel}{t14}</span>;
      $[46] = ctaLabel;
      $[47] = t15;
    } else {
      t15 = $[47];
    }
    let t16;
    if ($[48] !== CardLink || $[49] !== bodyEl || $[50] !== href || $[51] !== imageEl || $[52] !== t13 || $[53] !== t15) {
      t16 = <CardLink href={href} className={styles["helios-program-card__link"]} aria-label={t13}>{imageEl}{bodyEl}{t15}</CardLink>;
      $[48] = CardLink;
      $[49] = bodyEl;
      $[50] = href;
      $[51] = imageEl;
      $[52] = t13;
      $[53] = t15;
      $[54] = t16;
    } else {
      t16 = $[54];
    }
    let t17;
    if ($[55] !== rootClass || $[56] !== t16) {
      t17 = <div className={rootClass}>{t16}</div>;
      $[55] = rootClass;
      $[56] = t16;
      $[57] = t17;
    } else {
      t17 = $[57];
    }
    return t17;
  }
  let t13;
  if ($[58] !== bodyEl || $[59] !== imageEl || $[60] !== rootClass) {
    t13 = <div className={rootClass}>{imageEl}{bodyEl}</div>;
    $[58] = bodyEl;
    $[59] = imageEl;
    $[60] = rootClass;
    $[61] = t13;
  } else {
    t13 = $[61];
  }
  return t13;
}
export default ProgramCard;